export interface FormTokenValue {
  token: string;
  csrfToken?: string;
  received: number;
}

/** Owns one form's token; an attempted operation must survive an ambiguous network failure. */
export class FormTokenSession {
  private current: Promise<FormTokenValue> | null = null;
  private received = 0;
  private attempted = false;
  private loading = false;

  /** Refreshes only unused tokens, never silently creating a new operation after an uncertain POST. */
  get(load: () => Promise<Omit<FormTokenValue, "received">>, now = Date.now): Promise<FormTokenValue> {
    if (this.current === null || (!this.loading && !this.attempted && now() - this.received > 50 * 60 * 1000)) {
      this.loading = true;
      this.current = Promise.resolve().then(load).then((value) => {
        this.received = now();
        this.loading = false;
        return { ...value, received: this.received };
      }).catch((error: unknown) => {
        this.current = null;
        this.loading = false;
        throw error;
      });
    }
    return this.current;
  }

  /** Pins the operation identity before a request can reach the server. */
  markAttempted(): void {
    this.attempted = true;
  }
}

/**
 * Names the CSRF header a session-bound form posts with, taken from its own markup.
 * Returns null for forms that still use the timestamp token and send no header.
 */
export function csrfHeader(dataset: Readonly<Record<string, string | undefined>>): [name: string, value: string] | null {
  const name = dataset["csrfHeader"];
  return name === undefined || name.length === 0 ? null : [name, dataset["csrfToken"] ?? ""];
}
